// src/locales/en.js
// VEGA ROBOTICS - English Translations

export default {
  // ============================================================
  // GENERAL
  // ============================================================
  general: {
    company: 'Vega Robotics',
    tagline: 'Artificial Intelligence for Industry',
    learnMore: 'Learn more',
    contactUs: 'Contact Us',
    getStarted: 'Get Started',
    readMore: 'Read More',
    viewAll: 'View All',
    loading: 'Loading...',
    error: 'An error occurred',
    success: 'Success!',
    send: 'Send',
    cancel: 'Cancel',
    confirm: 'Confirm',
    back: 'Back',
    next: 'Next',
    previous: 'Previous',
    close: 'Close',
    search: 'Search',
    filter: 'Filter',
    sortBy: 'Sort by',
    all: 'All',
    none: 'None',
    yes: 'Yes',
    no: 'No',
    scheduleDemo: 'Schedule a Demo',
    scheduleMeeting: 'Schedule Meeting',
    requestQuote: 'Request Quote',
  },

  // ============================================================
  // HEADER / NAVIGATION
  // ============================================================
  nav: {
    home: 'Home',
    about: 'About',
    solutions: 'Solutions',
    testimonials: 'Testimonials',
    blog: 'Blog',
    contact: 'Contact',
    investors: 'Investors',
    careers: 'Careers',
    products: 'Products',
    services: 'Services',
    partners: 'Partners',
  },

  // ============================================================
  // HERO SECTION
  // ============================================================
  hero: {
    eyebrow: 'Brazilian technology for global industry',
    titleStatic: 'Solutions for those who',
    title: 'Computer Vision for Industry 4.0',
    subtitle: 'Transform your production line with artificial intelligence. Detect defects in real-time and increase quality.',
    button: 'Explore Our Solutions',
    cta: 'Schedule a Demo',
    ctaSecondary: 'Explore Our Solutions',
    trusted: 'Trusted by leading companies',
    verbs: [
      'transform',
      'automate',
      'produce',
      'assemble',
      'stamp',
      'organize',
      'control',
      'deliver',
    ],
  },

  // ============================================================
  // SOLUTIONS
  // ============================================================
  solutions: {
    title: 'Our solutions',
    subtitle: 'Cutting-edge technology to transform your industrial operation',
    
    engenhariaAplicada: {
      name: 'Applied Engineering',
      description: 'On-demand projects for your industrial operation',
    },
    automacaoIndustrial: {
      name: 'Industrial Automation',
      description: 'Cells and systems that increase productivity and precision',
    },
    qualidadeInteligente: {
      name: 'Intelligent Quality',
      description: 'AI-powered visual inspection for zero defects',
    },
    agentesIndustriais: {
      name: 'Industrial Agents',
      description: 'Generative AI applied to your processes',
    },
    
    vixem: {
      name: 'VIXEM',
      tagline: 'Edge Computer Vision',
      description: 'Visual inspection system with embedded AI for real-time defect detection on the production line.',
      features: [
        'Defect detection in milliseconds',
        'Works offline (edge computing)',
        'MES/ERP system integration',
        'Real-time quality dashboard',
      ],
    },
    
    cadpro: {
      name: 'CADPro',
      tagline: 'Engineering Services',
      description: 'Tooling projects, devices, and industrial automation with high-precision engineering.',
      features: [
        'Stamping dies and molds design',
        'Control devices',
        'Process automation',
        'Specialized technical consulting',
      ],
    },
    
    proagentes: {
      name: 'PROAgentes.ai',
      tagline: 'AI Agents',
      description: 'Intelligent agents platform for business process automation and customer service.',
      features: [
        'Conversational agents',
        'Task automation',
        'System integration',
        'Intelligent data analysis',
      ],
    },
  },

  // ============================================================
  // TESTIMONIALS
  // ============================================================
  testimonials: {
    title: 'What they say about us',
    subtitle: 'See how we are transforming Brazilian industry',
    prev: 'Previous',
    next: 'Next',
    goToSlide: 'Go to slide',
    
    items: [
      {
        text: 'Top quality professionals! Unmatched service.',
        author: 'Rodrigo',
      },
      {
        text: 'Cutting-edge company in automation and robotics. High-quality work, and the team demonstrates great technical competence. I recommend it to anyone looking for innovation and reliable partners in the technology sector.',
        author: 'Anderson Fernandes',
      },
      {
        text: 'Excellent company. Has great service and reliability.',
        author: 'Danilo Carvalho Souza',
      },
      {
        text: 'I strongly recommend this company for anyone seeking innovation, quality in computer vision and AI. Vega Robotics delivers cutting-edge solutions for industry with a commitment to excellence.',
        author: 'Sttela Bastos',
      },
    ],
  },

  // ============================================================
  // CTA
  // ============================================================
  cta: {
    title: 'Ready to transform your industry?',
    subtitle: 'Talk to our specialists. We respond within 24h.',
  },

  // ============================================================
  // BLOG
  // ============================================================
  blog: {
    title: 'Content that transforms',
    subtitle: 'Articles, cases, and news about AI, automation, and industry 4.0',
    readTime: 'min read',
    viewAll: 'View all articles',
    noPosts: 'No articles found.',
    loadMore: 'Load More',

    // Blog Page
    pageTitle: 'Vega Robotics Blog',
    pageSubtitle: 'Articles about technology, innovation, artificial intelligence, and the future of Brazilian industry.',
    allCategories: 'All',
    noPostsTitle: 'No posts found',
    noPostsText: 'New content coming soon!',
    noPostsCategory: 'No posts in this category yet.',
    readMore: 'Read more',
    views: 'views',
    backToBlog: 'Back to Blog',
    share: 'Share',

    categories: {
      all: 'All',
      computerVision: 'Computer Vision',
      edgeComputing: 'Edge Computing',
      ai: 'Artificial Intelligence',
      industry: 'Industry 4.0',
      vision: 'Computer Vision',
      automation: 'Automation',
      news: 'News',
      tecnologia: 'Technology',
      industria: 'Industry 4.0',
      ia: 'Artificial Intelligence',
      inovacao: 'Innovation',
      cases: 'Success Cases',
      tutoriais: 'Tutorials',
    },

    posts: [
      {
        title: 'How Computer Vision is transforming industrial quality',
        excerpt: 'Discover how AI systems are detecting defects invisible to the human eye and reducing losses by up to 40%.',
        date: 'Dec 04, 2024',
      },
      {
        title: 'Edge Computing in Industry: Why process data at the edge?',
        excerpt: 'Understand the advantages of processing data locally and how it impacts latency, security, and operational costs.',
        date: 'Nov 27, 2024',
      },
      {
        title: 'Multi-Agent Systems: The future of intelligent automation',
        excerpt: 'How collaborative AI agents are revolutionizing industrial processes and making real-time decisions.',
        date: 'Nov 19, 2024',
      },
    ],
  },

  // ============================================================
  // CONTACT FORM (HubSpot Modal)
  // ============================================================
  contactForm: {
    title: 'Request',
    titleHighlight: 'Quote',
    name: 'Name',
    namePlaceholder: 'Your full name',
    email: 'Email',
    emailPlaceholder: 'your@email.com',
    phone: 'Phone',
    phonePlaceholder: '+1 (555) 123-4567',
    company: 'Company',
    companyPlaceholder: 'Company name (optional)',
    message: 'Describe your project',
    messagePlaceholder: 'Tell us a little about the project you need to develop...',
    submit: 'Send Request',
    sending: 'Sending...',
    successTitle: 'Message Sent!',
    successText: 'We received your request and will contact you within 24 business hours.',
    errorRequired: 'Please fill in all required fields.',
    errorGeneral: 'An error occurred while sending. Please try again or contact us via WhatsApp.',
    privacy: 'By submitting, you agree to our privacy policy. Your data is secure and will not be shared.',
  },

  // ============================================================
  // CONTACT
  // ============================================================
  contact: {
    title: 'Get in Touch',
    subtitle: 'We are ready to help your company evolve',
    
    form: {
      name: 'Full name',
      namePlaceholder: 'Your name',
      email: 'Email',
      emailPlaceholder: 'your@email.com',
      phone: 'Phone',
      phonePlaceholder: '+1 (555) 123-4567',
      company: 'Company',
      companyPlaceholder: 'Your company name',
      subject: 'Subject',
      subjectPlaceholder: 'How can we help?',
      message: 'Message',
      messagePlaceholder: 'Describe your needs...',
      submit: 'Send Message',
      sending: 'Sending...',
      successMessage: 'Message sent successfully! We will get back to you soon.',
      errorMessage: 'Error sending message. Please try again.',
    },
    
    info: {
      address: 'Address',
      addressValue: 'São Paulo, SP - Brazil',
      email: 'Email',
      emailValue: 'contact@vegarobotics.com.br',
      phone: 'Phone',
      phoneValue: '+55 (11) 99999-9999',
      hours: 'Hours',
      hoursValue: 'Mon - Fri: 9am to 6pm',
    },
  },

  // ============================================================
  // FOOTER
  // ============================================================
  footer: {
    description: 'Artificial intelligence for Brazilian industry. Computer vision, multi-agent systems and edge computing.',
    solutions: 'Solutions',
    contact: 'Contact',
    copyright: '© {year} Vega Robotics. All rights reserved.',
    privacy: 'Privacy',
    terms: 'Terms of Use',
    
    links: {
      title: 'Quick Links',
      solutions: 'Solutions',
      contact: 'Contact',
      privacy: 'Privacy',
      terms: 'Terms of Use',
    },
    
    social: {
      title: 'Social Media',
      followUs: 'Follow us',
    },
  },

  // ============================================================
  // ERROR PAGES
  // ============================================================
  errors: {
    notFound: {
      title: 'Page Not Found',
      description: 'The page you are looking for does not exist or has been moved.',
      backHome: 'Back to Home',
    },
    serverError: {
      title: 'Server Error',
      description: 'An unexpected error occurred. Please try again later.',
      retry: 'Try Again',
    },
  },

  // ============================================================
  // FORM VALIDATION
  // ============================================================
  validation: {
    required: 'This field is required',
    email: 'Enter a valid email',
    phone: 'Enter a valid phone number',
    minLength: 'Minimum {min} characters',
    maxLength: 'Maximum {max} characters',
  },

  // ============================================================
  // ACCESSIBILITY
  // ============================================================
  accessibility: {
    skipToContent: 'Skip to content',
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
    toggleLanguage: 'Change language',
    darkMode: 'Dark mode',
    lightMode: 'Light mode',
    backToTop: 'Back to top',
    play: 'Play',
    pause: 'Pause',
    clientsAriaLabel: 'Companies that trust Vega',
    whatsappAriaLabel: 'Chat on WhatsApp',
  },

  // ============================================================
  // CAREERS
  // ============================================================
  careers: {
    pageTitle: 'Join Our Team',
    pageSubtitle: 'Join a team passionate about technology and innovation. Build the future of Brazilian industry.',
    openPositions: 'Open Positions',
    departments: 'Departments',
    searchPlaceholder: 'Search jobs...',
    allDepartments: 'All Departments',
    allModalities: 'All Work Models',
    viewJob: 'View Job',
    applyNow: 'Apply Now',
    backToJobs: 'Back to Jobs',
    aboutRole: 'About the Role',
    requirements: 'Requirements',
    benefits: 'Benefits',
    share: 'Share',
    noJobsTitle: 'No jobs found',
    noJobsText: 'We currently have no open positions. Follow us on social media to stay updated.',
    noJobsFiltered: 'No jobs match the selected filters.',
    notFoundTitle: 'Job not found',
    notFoundText: 'The job you are looking for does not exist or has been closed.',

    types: {
      fullTime: 'Full-time',
      partTime: 'Part-time',
      contract: 'Contract',
      internship: 'Internship',
      freelance: 'Freelance',
    },

    modalities: {
      onSite: 'On-site',
      remote: 'Remote',
      hybrid: 'Hybrid',
    },

    experience: {
      intern: 'Intern',
      junior: 'Junior',
      mid: 'Mid-level',
      senior: 'Senior',
      lead: 'Lead/Manager',
    },

    form: {
      name: 'Full Name',
      namePlaceholder: 'Your name',
      email: 'Email',
      emailPlaceholder: 'your@email.com',
      phone: 'Phone',
      phonePlaceholder: '+55 (11) 99999-9999',
      linkedin: 'LinkedIn',
      portfolio: 'Portfolio / GitHub',
      coverLetter: 'Cover Letter',
      coverLetterPlaceholder: 'Tell us about yourself and why you are the ideal candidate for this position...',
      submit: 'Submit Application',
      sending: 'Sending...',
      successTitle: 'Application Sent!',
      successMessage: 'We received your application and will get back to you soon.',
      errorMessage: 'An error occurred. Please try again.',
    },
  },

  // ============================================================
  // WHATSAPP
  // ============================================================
  whatsapp: {
    defaultMessage: 'Hello! I would like to know more about Vega Robotics solutions.',
  },
};
